import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@porchpass/ui";
import { AlertCircle, MoreHorizontal } from "lucide-react";
import { useState } from "react";
import type { DomainTaskStatusType } from "@porchpass/api-client";
import { isOnLetter, needsCorrection } from "src/api/preApproval";
import type { PreApprovalCondition } from "src/api/preApproval/types";
import { PreApprovalBadge } from "../../components/PreApprovalBadge";
import { formatDate, formatStatusLabel } from "../../utils";
import { ConditionDescriptions } from "./ConditionDescriptions";
import {
  ConditionStatusDropdown,
  STATUS_STYLES,
} from "./ConditionStatusDropdown";

interface ConditionCardProps {
  condition: PreApprovalCondition;
  onStatusChange: (status: DomainTaskStatusType) => void;
  onEdit: () => void;
  onRemoveFromLetter: () => void | Promise<void>;
}

export function ConditionCard({
  condition,
  onStatusChange,
  onEdit,
  onRemoveFromLetter,
}: ConditionCardProps) {
  const [isRemoving, setIsRemoving] = useState(false);
  const status = condition.status ?? "not_done";
  const onLetter = isOnLetter(status);
  const showCorrection = needsCorrection(status);

  const handleRemove = () => {
    setIsRemoving(true);
    void Promise.resolve(onRemoveFromLetter()).finally(() => {
      setIsRemoving(false);
    });
  };

  return (
    <div className="rounded-xl border border-[#E4E7EC] bg-white p-3.5 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4">
        <ConditionStatusDropdown
          status={status}
          onStatusChange={onStatusChange}
          triggerClassName={STATUS_STYLES[status]}
          label={formatStatusLabel(status)}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="More actions"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>Edit condition</DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleRemove}
              disabled={isRemoving || status === "cancelled"}
              className="text-[#B91C1C]"
            >
              Remove from letter
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1.5">
          <p className="font-medium text-[#101827]">{condition.title}</p>
          <PreApprovalBadge variant={onLetter ? "on-letter" : "off-letter"}>
            {onLetter ? "On letter" : "Off letter"}
          </PreApprovalBadge>
          {showCorrection && (
            <PreApprovalBadge
              variant="correction"
              icon={<AlertCircle className="h-3 w-3" />}
            >
              Correction Needed
            </PreApprovalBadge>
          )}
          {!condition.digifi_synced && (
            <PreApprovalBadge variant="pending-sync">
              Pending sync
            </PreApprovalBadge>
          )}
        </div>
        <ConditionDescriptions
          internal_description={condition.internal_description}
          external_description={condition.external_description}
        />
        {condition.updated_at && (
          <p className="text-xs text-[#9AA2B2] mt-1.5">
            Updated {formatDate(condition.updated_at)}
          </p>
        )}
      </div>
    </div>
  );
}
